import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../../redux/hooks';
import { usePatientQuestionnaire } from './hooks';
import queryClient from '../../../lib/queryClient';

import QuestionCreatorComponent from './component';
import TaskCompleted from '../../../components/shared/TaskCompleted';

import { getUnansweredQuestion } from '../../../utils/getUnansweredQuestion';
import { calculateBMI } from '../../../utils/calculateBmi';

import { MULTITEXT_SUBQUESTION, QUESTION_TYPE } from '../../../constants';
import MESSAGES from '../../../constants/message';

import { IQuestion, IQuestionCreatorProps, TYesNoAnswer } from '../types';

import imgLabExperiment from '../../../../public/images/lab-experiment.svg';

const QuestionCreator: React.FC<IQuestionCreatorProps> = ({
  questionnaires,
  historyType,
}) => {
  const navigate = useNavigate();
  const { id: patientId } =
    useAppSelector((state) => state.auth.currentUser) || {};

  const { submitPatientQuestionnaire } = usePatientQuestionnaire();

  const [activeStep, setActiveStep] = useState(
    getUnansweredQuestion(questionnaires)
  );

  const [tempAnswer, setTempAnswer] = useState<string>('');
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [disableBack, setDisableBack] = useState<boolean>(activeStep === 0);
  const [shouldRenderSubQuestion, setShouldRenderSubQuestion] =
    useState<boolean>(false);

  const [currentQuestion, setCurrentQuestion] = useState<IQuestion>(
    questionnaires[activeStep]
  );

  const isCompleted = useMemo(
    () => questionnaires.length === activeStep,
    [questionnaires.length, activeStep]
  );

  const navigateToTasks = () => {
    navigate('/tasks');
    queryClient.invalidateQueries({
      queryKey: [`${historyType}questionnaires-list`],
    });
  };

  const submitAnswer = (question: IQuestion) => {
    submitPatientQuestionnaire(patientId, historyType, [
      {
        id: question.id,
        answer: question.answer,
      },
    ]);
  };

  const addMoreMultiTextAnswer = (answer: TYesNoAnswer) => {
    if (answer == 'No') {
      setActiveStep((prevStep: number) => prevStep + 1);
      return;
    }

    setCurrentQuestion(questionnaires[activeStep]);
  };

  const handleBack = useCallback(() => {
    if (shouldRenderSubQuestion) {
      setDisableSubmit(questionnaires[activeStep].answer === '');
      setDisableBack(activeStep === 0);
      setShouldRenderSubQuestion(false);
      if (currentQuestion.id === questionnaires[activeStep].id) {
        setCurrentQuestion(questionnaires[activeStep - 1]);
        setActiveStep((prev: number) => prev - 1);
      } else {
        setCurrentQuestion(questionnaires[activeStep]);
      }
    } else {
      setActiveStep(activeStep - 1);
    }
  }, [shouldRenderSubQuestion, currentQuestion, activeStep, questionnaires]);

  const subQuestionChecker = () => {
    const hasSubQuestions = currentQuestion.sub_questions?.length > 0;

    if (hasSubQuestions) {
      const nextQuestion = currentQuestion.sub_questions.find(
        (item) =>
          item.condition === currentQuestion.answer || item.condition === 'All'
      );
      setShouldRenderSubQuestion(nextQuestion?.question === '');
    }
  };

  const setAnswer = (
    answer: string | string[],
    childQuestion: boolean = false
  ) => {
    if (
      currentQuestion.question_type === QUESTION_TYPE.RADIO &&
      questionnaires[activeStep].question_type === QUESTION_TYPE.MULTITEXT
    ) {
      addMoreMultiTextAnswer(answer as TYesNoAnswer);
    } else if (
      questionnaires[activeStep].question_type === QUESTION_TYPE.MULTITEXT
    ) {
      setTempAnswer(answer as string);
    } else {
      if (
        !childQuestion &&
        currentQuestion.question_type === QUESTION_TYPE.RADIO &&
        currentQuestion.parent_question_id === null &&
        currentQuestion.sub_questions?.length > 0
      ) {
        currentQuestion.sub_questions.forEach((question) => {
          if (question.condition === 'All' || question.condition === answer) {
            setDisableSubmit(question?.answer === '');
          } else {
            question.answer = '';
            submitAnswer(question);
          }
        });
      }
      if (childQuestion) {
        setDisableSubmit(answer === '');
        currentQuestion.sub_questions[0].answer = answer;
        const subQuestion = currentQuestion.sub_questions[0];
        submitAnswer(subQuestion);
      } else {
        if (currentQuestion.parent_question_id) setDisableSubmit(answer === '');
        currentQuestion.answer = answer;
        submitAnswer(currentQuestion);
        calculateBMI(currentQuestion, questionnaires, submitAnswer);

        const hasSubQuestions = currentQuestion.sub_questions?.length > 0;

        if (hasSubQuestions) {
          const subQuestions = currentQuestion.sub_questions;

          const nextQuestion = subQuestions.find(
            (item) => item.condition === 'All' || item.condition === answer
          );

          setShouldRenderSubQuestion(nextQuestion?.question === '');
          if (nextQuestion && nextQuestion?.question !== '') {
            if (nextQuestion.parent_question_id)
              setDisableSubmit(nextQuestion.answer === '');
            setDisableBack(false);
            setCurrentQuestion(nextQuestion);
          }
        }
      }
    }
  };

  const setMultiTextAnswer = (answer: string[]) => {
    currentQuestion.answer = answer;
    submitAnswer(currentQuestion);
  };

  const handleContinue = () => {
    if (
      questionnaires[activeStep].question_type === QUESTION_TYPE.MULTITEXT &&
      tempAnswer !== ''
    ) {
      currentQuestion.answer = [...(currentQuestion.answer as []), tempAnswer];
      setTempAnswer('');
      submitAnswer(currentQuestion);
      setCurrentQuestion(MULTITEXT_SUBQUESTION);
    } else {
      setActiveStep((prev: number) => prev + 1);
    }
  };

  const updateRenderSubQuestion = useCallback(() => {
    const question = questionnaires[activeStep];
    if (question) {
      const hasSubQuestions = question.sub_questions?.length > 0;

      if (hasSubQuestions) {
        const subQuestions = question.sub_questions;

        const nextQuestion = subQuestions.find(
          (item) =>
            item.condition === 'All' || item.condition === question.answer
        );

        setShouldRenderSubQuestion(nextQuestion?.question === '');
      }
    }
  }, [activeStep, questionnaires]);

  useEffect(() => {
    if (questionnaires[activeStep]?.parent_question_id)
      setDisableSubmit(questionnaires[activeStep].answer === '');
    setCurrentQuestion(questionnaires[activeStep]);
    setDisableBack(activeStep === 0);
    updateRenderSubQuestion();
  }, [activeStep, questionnaires, updateRenderSubQuestion]);

  useEffect(() => {
    if (currentQuestion) {
      subQuestionChecker();
    }
  });

  // If questionnaires is completed then show completed
  if (isCompleted) {
    return (
      <TaskCompleted
        title={MESSAGES.TASK.COMPLETED.TITLE}
        description={MESSAGES.TASK.COMPLETED.DESCRIPTION}
        buttonText={MESSAGES.TASK.COMPLETED.BUTTON_TEXT}
        imgSrc={imgLabExperiment}
        imgAlt="Lab Experiment"
        handleClick={navigateToTasks}
        handleClose={navigateToTasks}
      />
    );
  }

  return (
    <QuestionCreatorComponent
      activeStep={activeStep}
      question={currentQuestion}
      disableBack={disableBack}
      disableSubmit={disableSubmit}
      shouldRenderSubQuestion={shouldRenderSubQuestion}
      stepCount={questionnaires.length}
      handleBack={handleBack}
      handleClose={navigateToTasks}
      setAnswer={setAnswer}
      handleContinue={handleContinue}
      setMultiTextAnswer={setMultiTextAnswer}
    />
  );
};

export default QuestionCreator;
